import { getErrorFromFieldState } from '@utils/formHelpers';
import { ChangeEvent, memo, ReactNode } from 'react';
import { Control, FieldPath, FieldValues, useController, useFormContext } from 'react-hook-form';
import PureCheckbox from './CheckboxField';

type RHFCheckboxFieldProps<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  id: string;
  label?: string | ReactNode;
  labelWidth?: string | number;
  requiredLabelWidth?: string | boolean;
  isRequired?: boolean;
  additionalDescription?: string | ReactNode;
  onChange?: (checked: boolean) => void;
};

const RHFCheckboxFieldInner = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control: controlProp,
  onChange: onChangeProp,
  ...props
}: RHFCheckboxFieldProps<TFieldValues>) => {
  const formContext = useFormContext<TFieldValues>();
  const control = controlProp ?? formContext?.control;

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    onChange(checked);
    onChangeProp?.(checked);
  };

  return (
    <PureCheckbox
      {...props}
      name={name}
      value={!!value}
      onChange={handleChange}
      error={getErrorFromFieldState(error)}
    />
  );
};

export const RHFCheckboxField = memo(RHFCheckboxFieldInner) as typeof RHFCheckboxFieldInner;
